import { usePersEventCatalogData } from './composables/usePersEventCatalogData';
import useHeader from './header';

export default function useProduct({
  adaptive, store, router,
}) {
  const { getHeader } = useHeader({ store, router });
  const { filtersData, listingData, searchData } = usePersEventCatalogData({ store, router, adaptive });

  function sendProductAddToCartEvent({
    productId,
    skuId,
    slot,
    cart,
  }) {
    const products = Object.keys(cart.products).map((key) => {
      const product = cart.products[key];

      const result = {
        id: product.id,
        skuId: product.skuId,
        quantity: product.quantity,
      };

      result.models = product.models.map((model) => ({
        id: model.id,
        name: model.name,
        price: {
          discountPrice: model.price.discountPrice,
          discountAmount: model.price.discountAmount,
        },
        skus: model.skus,
      }));

      return result;
    });

    const body = {
      productId,
      skuId,
      slot,
      cart: {
        products,
      },
    };

    return store.$apiBFF.sendPersEvent({ type: 'product-add-to-cart', data: { header: getHeader(), body } });
  }

  function sendProductClickSearchResultsEvent({
    productId, productPosition, productDiscount = 0, productPrice = 0,
  }) {
    const product = {
      id: productId,
      position: productPosition,
      price: +productPrice,
      discount: +productDiscount,
    };

    const body = {
      product,
      search: searchData.value,
      listing: listingData.value,
      filters: filtersData.value,
    };

    return store.$apiBFF.sendPersEvent({ type: 'product-click-search-results', data: { header: getHeader(), body } });
  }

  function sendProductClickCatalogEvent({
    productId, productPosition, productDiscount = 0, productPrice = 0,
  }) {
    const product = {
      id: productId,
      position: productPosition,
      price: +productPrice,
      discount: +productDiscount,
    };

    const body = {
      product,
      listing: listingData.value,
      filters: filtersData.value,
    };

    return store.$apiBFF.sendPersEvent({ type: 'product-click-catalog', data: { header: getHeader(), body } });
  }

  function sendViewProductEvent({
    productId,
    productPrice = 0,
    productDiscount = 0,
    productName = '',
    productSkus = [],
  }) {
    const availableSkus = productSkus.filter(({ available } = {}) => available);
    const isDelivery = availableSkus.some(({ delivery } = {}) => delivery?.available);
    const isPickUp = availableSkus.some(({ pickup }) => !!pickup);
    const isPickPoint = availableSkus.some(({ thirdParty }) => thirdParty?.available);
    const isNow = availableSkus.some(({ pickup }) => pickup?.store?.storesCount > 0);

    const isAvailable = isDelivery || isPickUp || isPickPoint || isNow;

    const availability = isAvailable
      ? {
        isDelivery,
        isPickUp,
        isPickPoint,
        isNow,
      }
      : null;

    const sizeRatio = parseFloat(availableSkus.length / productSkus.length).toFixed(2);

    const product = {
      id: productId,
      siteId: productId,
      price: +productPrice,
      discount: +productDiscount,
      name: productName,
      sizeRatio: +sizeRatio,
      availability,
    };

    const body = {
      product,
    };

    return store.$apiBFF.sendBasePersEvent({ type: 'product-view', data: { header: getHeader(), body } });
  }

  function sendProductPickSizeEvent({
    product,
    activeSku,
    slot = null,
  }) {
    const availableSkus = product?.skus?.filter(({ available } = {}) => available);

    const isDelivery = availableSkus
      .some((sku) => (sku.availability ? sku.availability.delivery?.available : sku.delivery?.available));
    const isPickUp = availableSkus
      .some((sku) => (sku.availability
        ? (sku.availability.pickup?.available || sku.availability.transport?.available)
        : (sku.pickup?.store?.storesCount || sku.pickup?.delayed?.storesCount)));
    const isPickPoint = availableSkus
      .some((sku) => (sku.availability ? sku.availability.pointDelivery?.available : sku.thirdParty?.available));
    const isNow = availableSkus
      .some((sku) => (sku.availability
        ? sku.availability.pickup?.shopsCount > 0 : sku?.pickup?.store?.storesCount > 0));

    const availability = isDelivery || isPickUp ? {
      isDelivery,
      isPickUp,
      isPickPoint,
      isNow,
    } : null;

    const sizeRatio = parseFloat(availableSkus.length / product.skus.length).toFixed(2);
    const price = Number(product?.price?.discountPrice || product.discountPrice) || 0;
    const discount = Number(product?.price?.discountAmount || (product.basePrice - product.discountPrice)) || 0;

    const productBody = {
      id: product.id,
      siteId: product.id,
      price,
      discount,
      name: product.name,
      sizeRatio: +sizeRatio,
      availability,
      skuId: activeSku.id,
      skuCode: activeSku.code,
      size: activeSku.size,
    };

    const body = {
      product: productBody,
      slot,
    };

    return store.$apiBFF.sendBasePersEvent({ type: 'product-pick-size', data: { header: getHeader(), body } });
  }

  return {
    sendProductAddToCartEvent,
    sendProductClickSearchResultsEvent,
    sendProductClickCatalogEvent,
    sendViewProductEvent,
    sendProductPickSizeEvent,
  };
}
